// src/v2/hooks/useReportRF1.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportRF1 = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Scenario A: Download the raw PhilHealth template directly from public/docs/
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    const pdfUrl = `${window.location.origin}/docs/PhilHealth_RF1.pdf`;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `PhilHealth_RF1_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the PhilHealth RF-1 with data using pdf-lib
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      const pdfUrl = `${window.location.origin}/docs/PhilHealth_RF1.pdf`;
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template not found");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- COORDINATE MAPPING (Estimates for PhilHealth RF-1) ---
      // 1. Employer Information
      page.drawText(companyInfo?.name || "", { x: 100, y: 725, size: 10, font });
      page.drawText(companyInfo?.tin || "", { x: 100, y: 705, size: 10, font });

      // 2. Applicable Period
      page.drawText(`${month} ${year}`, { x: 480, y: 725, size: 10, font });

      // 3. Summary Calculation Example
      const totalEE = data.reduce((sum, item) => sum + (item.eeAmount || 0), 0);
      const totalER = data.reduce((sum, item) => sum + (item.erAmount || 0), 0);
      const grandTotal = totalEE + totalER;

      page.drawText(grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: 510, y: 310, size: 10, font });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PhilHealth_RF1_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("PhilHealth RF-1 generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('RF1', params);
        data = res || [];
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }
    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};